import { useEffect, useMemo, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  Tabs,
  Tab,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { Save, People } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import DetailHeader from '../components/DetailHeader';
import UserTelephonyAvailabilityStatus from '../components/users/UserTelephonyAvailabilityStatus';
import api from '../services/api';
import usePageTitle from '../hooks/usePageTitle';
import { usePermissions } from '../contexts/PermissionContext';
import { useAuth } from '../contexts/AuthContext';
import type { UserTelephonyAvailability } from '../types';

const initialFormData = {
  email: '',
  password: '',
  firstName: '',
  lastName: '',
  groupIds: [] as string[],
};

type TelephonyEndpointType = 'internal_extension' | 'external_extension' | 'external_phone';

interface UserTelephonyEndpointFormState {
  enabled: boolean;
  endpointType: TelephonyEndpointType;
  value: string;
  isActive: boolean;
}

const initialTelephonyEndpoint: UserTelephonyEndpointFormState = {
  enabled: false,
  endpointType: 'internal_extension',
  value: '',
  isActive: true,
};

interface GroupOption {
  id: string;
  name: string;
  isSystem?: boolean;
  scope?: 'system' | 'tenant';
}

interface UserFormFieldErrors {
  email?: string;
  password?: string;
  groupIds?: string;
  tenantId?: string;
  telephonyEndpoint?: string;
}

function extractApiMessage(value: unknown): string | undefined {
  if (typeof value === 'string') {
    return value;
  }
  if (Array.isArray(value)) {
    return extractApiMessage(value[0]);
  }
  return undefined;
}

function extractApiFieldError(payload: unknown, fieldName: string): string | undefined {
  if (!payload || typeof payload !== 'object') {
    return undefined;
  }
  const record = payload as Record<string, unknown>;
  const direct = extractApiMessage(record[fieldName]);
  if (direct) {
    return direct;
  }
  if (record.detail && typeof record.detail === 'object') {
    return extractApiMessage((record.detail as Record<string, unknown>)[fieldName]);
  }
  return undefined;
}

export default function UserFormPage() {
  const { t } = useTranslation(['pages', 'common']);
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = Boolean(id);
  const { hasPermission } = usePermissions();
  const { isSuperuser, subscriptions } = useAuth();
  const canRead = hasPermission('users_read');
  const canWrite = hasPermission(isEditing ? 'users_update' : 'users_create');
  const canAssignGroups = hasPermission('users_assign_groups');
  const canEditTerminal = isSuperuser;

  const [groups, setGroups] = useState<GroupOption[]>([]);
  const [assignedGroups, setAssignedGroups] = useState<GroupOption[]>([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [groupsLoadError, setGroupsLoadError] = useState<string | null>(null);
  const [formData, setFormData] = useState(initialFormData);
  const [telephonyEndpoint, setTelephonyEndpoint] = useState<UserTelephonyEndpointFormState>(initialTelephonyEndpoint);
  const [telephonyAvailability, setTelephonyAvailability] = useState<UserTelephonyAvailability | null>(null);
  const [activeTab, setActiveTab] = useState<'basic' | 'terminal'>('basic');
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<UserFormFieldErrors>({});
  const [createBlockedMessage, setCreateBlockedMessage] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [availabilityNowMs, setAvailabilityNowMs] = useState(() => Date.now());
  const nameParts = [formData.firstName, formData.lastName].filter(Boolean);
  const descriptor = isEditing
    ? nameParts.join(' ') || formData.email || userId || undefined
    : undefined;
  usePageTitle(t('pages:users.title'), descriptor);

  const groupOptionsById = useMemo(() => {
    const next = new Map<string, GroupOption>();
    [...assignedGroups, ...groups].forEach((group) => {
      next.set(group.id, group);
    });
    return next;
  }, [assignedGroups, groups]);

  const fetchGroupsRef = useRef<() => Promise<void>>(async () => undefined);
  const fetchUserRef = useRef<(userId: string) => Promise<void>>(async () => undefined);

  useEffect(() => {
    if (canAssignGroups) {
      void fetchGroupsRef.current();
      return;
    }
    setGroups([]);
    setGroupsLoading(false);
    setGroupsLoadError(null);
  }, [canAssignGroups]);

  useEffect(() => {
    if (isEditing && id) {
      void fetchUserRef.current(id);
    }
  }, [id, isEditing]);

  useEffect(() => {
    if (!telephonyAvailability?.isEligible || telephonyAvailability.isAvailable) {
      return undefined;
    }
    const intervalId = window.setInterval(() => setAvailabilityNowMs(Date.now()), 1000);
    return () => window.clearInterval(intervalId);
  }, [telephonyAvailability]);

  useEffect(() => {
    if (!canEditTerminal && activeTab === 'terminal') {
      setActiveTab('basic');
    }
  }, [activeTab, canEditTerminal]);

  useEffect(() => {
    if (isEditing || !canWrite) return;
    setSubscriptionLoading(true);
    try {
      const userSeats = subscriptions?.ai?.summary?.userSeats
        ?? subscriptions?.reverseIvr?.summary?.userSeats
        ?? null;
      if (userSeats?.isEnforced && !userSeats.canCreate) {
        setCreateBlockedMessage(
          t('pages:users.userSeats.limitReachedForm', {
            used: userSeats.used ?? 0,
            limit: userSeats.limit ?? 0,
          }),
        );
      } else {
        setCreateBlockedMessage(null);
      }
    } finally {
      setSubscriptionLoading(false);
    }
  }, [canWrite, isEditing, subscriptions, t]);

  const fetchGroups = async () => {
    setGroupsLoading(true);
    setGroupsLoadError(null);
    try {
      const response = await api.get('/api/users/assignable-groups/');
      const data = response.data.results ?? response.data;
      const normalized = Array.isArray(data)
        ? data.map((group: { id: string; name: string; isSystem?: boolean; scope?: 'system' | 'tenant' }) => ({
            id: group.id,
            name: group.name,
            isSystem: group.isSystem,
            scope: group.scope,
          }))
        : [];
      setGroups(normalized);
    } catch (err: unknown) {
      console.error('Error loading groups:', err);
      setGroups([]);
      const status = (err as { response?: { status?: number } })?.response?.status;
      setGroupsLoadError(
        status === 403
          ? t('pages:users.groupsAssignForbidden')
          : t('pages:users.groupsLoadError'),
      );
    } finally {
      setGroupsLoading(false);
    }
  };

  const fetchUser = async (userId: string) => {
    try {
      const response = await api.get(`/api/users/${userId}/`);
      const user = response.data as {
        id: string;
        email: string;
        firstName: string;
        lastName: string;
        groups?: { id: string; name: string }[];
        telephonyEndpoint?: {
          endpointType: TelephonyEndpointType;
          extension?: string;
          externalPhoneE164?: string;
          isActive?: boolean;
        } | null;
        telephonyAvailability?: UserTelephonyAvailability | null;
      };
      setFormData({
        email: user.email,
        password: '',
        firstName: user.firstName,
        lastName: user.lastName,
        groupIds: user.groups?.map((group) => group.id) || [],
      });
      setAssignedGroups(
        (user.groups || []).map((group) => ({
          id: group.id,
          name: group.name,
          scope: 'tenant',
        })),
      );
      setUserId(user.id ?? null);
      if (user.telephonyEndpoint) {
        const endpointType = user.telephonyEndpoint.endpointType;
        const endpointValue = endpointType === 'external_phone'
          ? user.telephonyEndpoint.externalPhoneE164 ?? ''
          : user.telephonyEndpoint.extension ?? '';
        setTelephonyEndpoint({
          enabled: true,
          endpointType,
          value: endpointValue,
          isActive: user.telephonyEndpoint.isActive ?? true,
        });
      } else {
        setTelephonyEndpoint(initialTelephonyEndpoint);
      }
      setTelephonyAvailability(user.telephonyAvailability ?? null);
    } catch (err) {
      console.error('Error loading user:', err);
      setError(t('pages:users.errors.loading') || 'Failed to load user');
    } finally {
      setLoading(false);
    }
  };

  fetchGroupsRef.current = fetchGroups;
  fetchUserRef.current = fetchUser;

  const handleSubmit = async (event?: FormEvent) => {
    event?.preventDefault();
    if (!canWrite) {
      setError(t('pages:users.accessDeniedWrite'));
      return;
    }
    if (!isEditing && createBlockedMessage) {
      setError(createBlockedMessage);
      return;
    }
    setSaving(true);
    setError(null);
    setFieldErrors({});

    try {
      const payload: Record<string, unknown> = { ...formData };
      if (isEditing && !formData.password) {
        delete payload.password;
      }
      if (!canAssignGroups) {
        delete payload.groupIds;
      }
      if (canEditTerminal) {
        if (telephonyEndpoint.enabled) {
          const endpointValue = telephonyEndpoint.value.trim();
          if (!endpointValue) {
            setError(t('pages:users.terminal.validation.valueRequired'));
            setSaving(false);
            return;
          }
          if (telephonyEndpoint.endpointType === 'external_phone' && !endpointValue.startsWith('+')) {
            setError(t('pages:users.terminal.validation.externalPhoneE164'));
            setSaving(false);
            return;
          }
          const endpointPayload: Record<string, unknown> = {
            endpointType: telephonyEndpoint.endpointType,
            isActive: telephonyEndpoint.isActive,
          };
          if (telephonyEndpoint.endpointType === 'external_phone') {
            endpointPayload.externalPhoneE164 = endpointValue;
          } else {
            endpointPayload.extension = endpointValue;
          }
          payload.telephonyEndpoint = endpointPayload;
        } else if (isEditing) {
          payload.telephonyEndpoint = null;
        }
      }

      if (isEditing && id) {
        await api.patch(`/api/users/${id}/`, payload);
      } else {
        await api.post('/api/users/', payload);
      }

      navigate('/users');
    } catch (err: unknown) {
      console.error('Error saving user:', err);
      const payload = (err as { response?: { data?: unknown } })?.response?.data;
      const nextFieldErrors = {
        email: extractApiFieldError(payload, 'email'),
        password: extractApiFieldError(payload, 'password'),
        groupIds: extractApiFieldError(payload, 'group_ids'),
        tenantId: extractApiFieldError(payload, 'tenant_id'),
        telephonyEndpoint: extractApiFieldError(payload, 'telephony_endpoint'),
      };
      setFieldErrors(nextFieldErrors);

      const nonFieldError = extractApiFieldError(payload, 'non_field_errors')
        || extractApiFieldError(payload, 'detail')
        || nextFieldErrors.tenantId;
      const hasFieldErrors = Object.values(nextFieldErrors).some(Boolean);
      setError(nonFieldError || (hasFieldErrors ? null : t('pages:users.errors.saving') || 'Failed to save user'));
    } finally {
      setSaving(false);
    }
  };

  const headerTitle = isEditing
    ? formData.email || t('pages:users.editTitle')
    : t('pages:users.newTitle');

  const breadcrumbs = [
    { label: t('common:menu.users'), to: '/users' },
    { label: isEditing ? t('pages:users.edit') : t('pages:users.new') },
  ];

  const headerActions = (
    <Button
      variant="contained"
      startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
      onClick={() => handleSubmit()}
      disabled={saving || subscriptionLoading || (!isEditing && Boolean(createBlockedMessage))}
    >
      {saving ? t('common:actions.saving') : t('common:actions.save')}
    </Button>
  );
  const terminalValueLabel = telephonyEndpoint.endpointType === 'external_phone'
    ? t('pages:users.terminal.externalPhoneLabel')
    : t('pages:users.terminal.extensionLabel');
  const terminalValueHelper = telephonyEndpoint.endpointType === 'external_phone'
    ? t('pages:users.terminal.externalPhoneHelper')
    : t('pages:users.terminal.extensionHelper');

  if (!canRead) {
    return <Alert severity="warning">{t('pages:users.accessDenied')}</Alert>;
  }

  if (!canWrite) {
    return <Alert severity="warning">{t('pages:users.accessDeniedWrite')}</Alert>;
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <DetailHeader
        title={headerTitle}
        sectionIcon={<People sx={{ fontSize: 32, color: 'primary.main' }} />}
        entityId={userId}
        breadcrumbs={breadcrumbs}
        actions={headerActions}
        backHref="/users"
      />

      {!isEditing && createBlockedMessage && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {createBlockedMessage}
        </Alert>
      )}

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Tabs value={activeTab} onChange={(_, next) => setActiveTab(next)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tab value="basic" label={t('pages:users.tabs.basic')} />
              {canEditTerminal && <Tab value="terminal" label={t('pages:users.tabs.terminal')} />}
            </Tabs>

            {activeTab === 'basic' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Typography variant="h6" fontWeight={600}>
                  {t('pages:users.basicInfo')}
                </Typography>
                <Divider />
                <TextField
                  fullWidth
                  label={t('pages:users.email')}
                  type="email"
                  value={formData.email}
                  onChange={(event) => {
                    setFieldErrors((prev) => ({ ...prev, email: undefined }));
                    setFormData({ ...formData, email: event.target.value });
                  }}
                  disabled={!isEditing && Boolean(createBlockedMessage)}
                  error={Boolean(fieldErrors.email)}
                  helperText={fieldErrors.email}
                  required
                />
                <TextField
                  fullWidth
                  label={isEditing ? t('pages:users.newPasswordLabel') : t('pages:users.password')}
                  type="password"
                  value={formData.password}
                  onChange={(event) => {
                    setFieldErrors((prev) => ({ ...prev, password: undefined }));
                    setFormData({ ...formData, password: event.target.value });
                  }}
                  disabled={!isEditing && Boolean(createBlockedMessage)}
                  error={Boolean(fieldErrors.password)}
                  helperText={fieldErrors.password || (isEditing ? t('pages:users.passwordHelper') : undefined)}
                />
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    fullWidth
                    label={t('pages:users.firstName')}
                    value={formData.firstName}
                    onChange={(event) => setFormData({ ...formData, firstName: event.target.value })}
                    disabled={!isEditing && Boolean(createBlockedMessage)}
                  />
                  <TextField
                    fullWidth
                    label={t('pages:users.lastName')}
                    value={formData.lastName}
                    onChange={(event) => setFormData({ ...formData, lastName: event.target.value })}
                    disabled={!isEditing && Boolean(createBlockedMessage)}
                  />
                </Box>
                <FormControl fullWidth error={Boolean(groupsLoadError || fieldErrors.groupIds)}>
                  <InputLabel>{t('pages:users.groups')}</InputLabel>
                  <Select
                    multiple
                    value={formData.groupIds}
                    onChange={(event) => {
                      setFieldErrors((prev) => ({ ...prev, groupIds: undefined }));
                      setFormData({ ...formData, groupIds: event.target.value as string[] });
                    }}
                    disabled={(!isEditing && Boolean(createBlockedMessage)) || !canAssignGroups || groupsLoading || Boolean(groupsLoadError)}
                    input={<OutlinedInput label={t('pages:users.groups')} />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((selectedId) => {
                          const group = groupOptionsById.get(selectedId);
                          return <Chip key={selectedId} label={group?.name || t('pages:users.groupUnavailable')} size="small" />;
                        })}
                      </Box>
                    )}
                  >
                    {groups.map((group) => (
                      <MenuItem key={group.id} value={group.id}>
                        <Checkbox checked={formData.groupIds.includes(group.id)} />
                        <ListItemText
                          primary={group.name}
                          secondary={group.isSystem ? t('pages:groups.table.system') : undefined}
                        />
                      </MenuItem>
                    ))}
                  </Select>
                  {groupsLoading && (
                    <FormHelperText>{t('pages:users.groupsLoading')}</FormHelperText>
                  )}
                  {fieldErrors.groupIds && (
                    <FormHelperText>{fieldErrors.groupIds}</FormHelperText>
                  )}
                  {!fieldErrors.groupIds && groupsLoadError && (
                    <FormHelperText>{groupsLoadError}</FormHelperText>
                  )}
                  {!fieldErrors.groupIds && !groupsLoadError && !canAssignGroups && (
                    <FormHelperText>{t('pages:users.groupsReadOnly')}</FormHelperText>
                  )}
                </FormControl>
              </Box>
            )}

            {canEditTerminal && activeTab === 'terminal' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="h6" fontWeight={600}>
                  {t('pages:users.terminal.title')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('pages:users.terminal.description')}
                </Typography>
                {isEditing && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                      p: 2,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: 'background.default',
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight={700}>
                      {t('pages:users.availability.title')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('pages:users.availability.description')}
                    </Typography>
                    <UserTelephonyAvailabilityStatus
                      availability={telephonyAvailability}
                      nowMs={availabilityNowMs}
                      showTimer
                      direction="column"
                      chipSize="medium"
                    />
                    <Typography variant="caption" color="text.secondary">
                      {!telephonyAvailability?.isEligible
                        ? t('pages:users.availability.noEndpointDetail')
                        : telephonyAvailability.isAvailable
                          ? t('pages:users.availability.availableHint')
                          : t('pages:users.availability.unavailableHint')}
                    </Typography>
                  </Box>
                )}
                <Divider />
                <FormControlLabel
                  control={(
                    <Switch
                      checked={telephonyEndpoint.enabled}
                      onChange={(event) => setTelephonyEndpoint((prev) => ({ ...prev, enabled: event.target.checked }))}
                      disabled={!canEditTerminal}
                    />
                  )}
                  label={t('pages:users.terminal.enable')}
                />
                <FormControl fullWidth disabled={!telephonyEndpoint.enabled || !canEditTerminal}>
                  <InputLabel>{t('pages:users.terminal.endpointType')}</InputLabel>
                  <Select
                    label={t('pages:users.terminal.endpointType')}
                    value={telephonyEndpoint.endpointType}
                    onChange={(event) => {
                      const nextType = event.target.value as TelephonyEndpointType;
                      setTelephonyEndpoint((prev) => ({ ...prev, endpointType: nextType, value: '' }));
                    }}
                  >
                    <MenuItem value="internal_extension">
                      {t('pages:users.terminal.endpointTypes.internalExtension')}
                    </MenuItem>
                    <MenuItem value="external_extension">
                      {t('pages:users.terminal.endpointTypes.externalExtension')}
                    </MenuItem>
                    <MenuItem value="external_phone">
                      {t('pages:users.terminal.endpointTypes.externalPhone')}
                    </MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  disabled={!telephonyEndpoint.enabled || !canEditTerminal}
                  label={terminalValueLabel}
                  value={telephonyEndpoint.value}
                  onChange={(event) => {
                    setFieldErrors((prev) => ({ ...prev, telephonyEndpoint: undefined }));
                    setTelephonyEndpoint((prev) => ({ ...prev, value: event.target.value }));
                  }}
                  error={Boolean(fieldErrors.telephonyEndpoint)}
                  helperText={fieldErrors.telephonyEndpoint || terminalValueHelper}
                />
                <FormControlLabel
                  control={(
                    <Switch
                      checked={telephonyEndpoint.isActive}
                      onChange={(event) => setTelephonyEndpoint((prev) => ({ ...prev, isActive: event.target.checked }))}
                      disabled={!telephonyEndpoint.enabled || !canEditTerminal}
                    />
                  )}
                  label={t('pages:users.terminal.isActive')}
                />
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
